import { AuthAwsError } from "@skilitics/auth-client";

import { type ErrorInfo, AuthError } from "./exports/error.ts";

// Re-throw AuthAwsError as translation-friendly AuthError.
// Use as `await someApi(...).catch(mapAwsError)`
export function mapAwsError(error: unknown): never {
  if (error instanceof AuthAwsError) {
    const info = defaultErrorInfo(error);
    if (info) {
      throw new AuthError(info.code, info.args);
    }
  }
  throw error;
}

const preSignUpErrorPrefix = "PreSignUp failed with error ";

export function defaultErrorInfo({ type, message }: AuthAwsError): ErrorInfo | null {
  switch (type) {
    // only if "preventUserExistenceErrors" is not set for sign in
    case "UserNotFoundException":
      return { code: "userNotFound" };

    // InitiateAuth
    // Apparently, happens regardless of "preventUserExistenceErrors" setting?
    case "UserNotConfirmedException":
      return { code: "userNotConfirmed" };

    // For reset password at least. Login uses "NotAuthorized: Password attempts exceeded"
    case "LimitExceededException":
      return { code: "attemptsExceeded" };

    // Kind of a catch-all
    case "NotAuthorizedException":
      switch (message) {
        // only if "preventUserExistenceErrors" is set for sign in
        case "Incorrect username or password.":
          return { code: "incorrectUserOrPassword" };
        // Don't know when these would happen: not for sign in
        // case "User is not confirmed.":
        //   return { code: "userNotConfirmed" };
        // case "User does not exist.":
        //   return { code: "userDoesNotExist" };
        // Happens regardless of "preventUserExistenceErrors" setting
        case "User is disabled.":
          return { code: "userDisabled" };
        case "Password attempts exceeded":
          return { code: "attemptsExceeded" };
        case "SignUp is not permitted for this user pool":
          return { code: "signUpNotPermitted" };
      }
      break;

    // InitiateAuth
    case "PasswordResetRequiredException":
      return { code: "resetRequired" };

    // ConfirmSignUp, maybe VerifyUserAttribute
    case "AliasExistsException":
      return { code: "userAlreadyExists" };

    // ConfirmSignUp at least
    case "ExpiredCodeException":
      return { code: "expiredCode" };

    // ConfirmSignUp and VerifyUserAttribute
    case "CodeMismatchException":
      switch (message) {
        case "Invalid verification code provided, please try again.":
          return { code: "invalidCode" };
        case "Exceed max verification code attempts. Please request new verification code and retry.":
          return { code: "attemptsExceeded" };
      }
      // No guarantee other messages have anything to do with invalid code...
      break;

    // SignUp
    case "UserLambdaValidationException":
      if (message.startsWith(preSignUpErrorPrefix)) {
        return {
          code: "signUpRejected",
          args: { message: message.substring(preSignUpErrorPrefix.length) },
        };
      }
      break;

    // ConfirmForgotPassword and SignUp
    case "InvalidPasswordException":
      switch (message) {
        // SignUp: slightly different messages from ConfirmForgotPassword
        case "Password did not conform with policy: Password not long enough":
          return { code: "passwordTooShort" };
        case "Password did not conform with policy: Password must have uppercase characters":
          return { code: "passwordRequiresUppercase" };
        case "Password did not conform with policy: Password must have lowercase characters":
          return { code: "passwordRequiresLowercase" };
        case "Password did not conform with policy: Password must have numeric characters":
          return { code: "passwordRequiresNumbers" };
        case "Password did not conform with policy: Password must have symbol characters":
          return { code: "passwordRequiresSymbols" };
        // ConfirmForgotPassword: slightly different messages from SignUp
        case "Password does not conform to policy: Password not long enough":
          return { code: "passwordTooShort" };
        case "Password does not conform to policy: Password must have uppercase characters":
          return { code: "passwordRequiresUppercase" };
        case "Password does not conform to policy: Password must have lowercase characters":
          return { code: "passwordRequiresLowercase" };
        case "Password does not conform to policy: Password must have numeric characters":
          return { code: "passwordRequiresNumbers" };
        case "Password does not conform to policy: Password must have symbol characters":
          return { code: "passwordRequiresSymbols" };
      }
      break;
  }

  return null;
}
